/*
 * Typed wrappers over the warm-transfer endpoints (PROD-10258).
 */

import { get_api as getApi, post_api as postApi } from '../../../../redux/api_funcs';
import { get_api_url as getApiUrl } from '../../../../utils/urls';

const BASE = '/v2/twilio_call';

// Same base-URL override as the rest of twilio_agent.
const getUrl = (endpoint: string): string =>
  import.meta.env.VITE_TWILIO_CALL_BASE_URL
    ? `${import.meta.env.VITE_TWILIO_CALL_BASE_URL}${endpoint}`
    : getApiUrl(endpoint);

interface Envelope<T> {
  data?: { data?: T };
}

// 'phone': no Hridaya tab open, so the transfer rings the colleague's own phone.
export type TransferTargetStatus = 'available' | 'phone' | 'on_call' | 'offline';

export type TransferChannel = 'browser' | 'phone';

export interface TransferTarget {
  uuid: string;
  name: string;
  email: string;
  status: TransferTargetStatus;
}

export type TransferStatus =
  | 'ringing'
  | 'accepted'
  | 'completed'
  | 'returned'
  | 'declined'
  | 'timed_out'
  | 'cancelled'
  | 'failed';

export interface StartedTransfer {
  transfer_uuid: string;
  status: TransferStatus;
  channel?: TransferChannel;
  expires_at: number;
}

export interface TransferDetails {
  transfer_uuid: string;
  call_uuid: string;
  status: TransferStatus;
  channel?: TransferChannel;
  from_agent_name: string;
  to_agent_name: string;
  student_name: string;
  student_uuid?: string;
  from_number: string;
  note: string;
  expires_at: number;
}

export const fetchTransferTargets = (callUUID: string): Promise<TransferTarget[]> =>
  getApi(getUrl(`${BASE}/transfer_targets?call_uuid=${encodeURIComponent(callUUID)}`), true).then(
    (res: Envelope<{ agents?: TransferTarget[] }>) => res?.data?.data?.agents || []
  );

export const startTransfer = (
  callUUID: string,
  toOpsUUID: string,
  note: string
): Promise<StartedTransfer> =>
  postApi(
    getUrl(`${BASE}/transfer`),
    { call_uuid: callUUID, to_ops_uuid: toOpsUUID, note },
    true
  ).then((res: Envelope<StartedTransfer>) => res?.data?.data as StartedTransfer);

export const fetchTransferByUUID = (transferUUID: string): Promise<TransferDetails> =>
  getApi(getUrl(`${BASE}/transfer?transfer_uuid=${encodeURIComponent(transferUUID)}`), true).then(
    (res: Envelope<TransferDetails>) => res?.data?.data as TransferDetails
  );

export const fetchTransferByCallSid = (callSid: string): Promise<TransferDetails> =>
  getApi(getUrl(`${BASE}/transfer?call_sid=${encodeURIComponent(callSid)}`), true).then(
    (res: Envelope<TransferDetails>) => res?.data?.data as TransferDetails
  );

const stop = (path: string, transferUUID: string): Promise<TransferStatus> =>
  postApi(getUrl(`${BASE}/${path}`), { transfer_uuid: transferUUID }, true).then(
    (res: Envelope<{ status: TransferStatus }>) => res?.data?.data?.status as TransferStatus
  );

export const cancelTransfer = (transferUUID: string): Promise<TransferStatus> =>
  stop('transfer/cancel', transferUUID);

export const declineTransfer = (transferUUID: string): Promise<TransferStatus> =>
  stop('transfer/decline', transferUUID);

// Server failure envelope is {status, reason, message}; never hand an object to the toast.
export const transferErrorReason = (err: unknown, fallback: string): string => {
  const data = (err as { response?: { data?: { reason?: unknown; message?: unknown } } })?.response
    ?.data;
  const reason = data?.reason ?? data?.message;
  return typeof reason === 'string' && reason ? reason : fallback;
};
